import type { Shift, StaffMember, Weekday } from '../types';
import { paidHoursRounded } from './nzRosterBreaks';

export type RosterDayCost = { hours: number; cost: number };

/** Same eligibility as Staff Roster grid / daily cost footer. */
export function isRosterEligibleStaff(member: StaffMember): boolean {
  return (
    member.role !== 'admin' &&
    member.role !== 'third_party_tester' &&
    member.includeInRoster !== false
  );
}

export function getStaffRosterKey(member: { id: number; firestoreId?: string }): string {
  return member.firestoreId ?? `staff-${member.id}`;
}

function dateKeyFromShiftDate(dateMs: number): string {
  return new Date(dateMs).toLocaleDateString('en-CA');
}

/**
 * Daily roster labour totals: paidHoursRounded(shift) × staff.hourlyRate.
 * Matches Staff Roster table footer; keyed by shift calendar date (YYYY-MM-DD).
 */
export function computeRosterCostByDateKey(
  shifts: readonly Shift[],
  staffMembers: readonly StaffMember[],
): Map<string, RosterDayCost> {
  const eligible = staffMembers.filter(isRosterEligibleStaff);
  const eligibleKeys = new Set(eligible.map(getStaffRosterKey));
  const staffByKey = new Map(eligible.map((m) => [getStaffRosterKey(m), m]));
  const totals = new Map<string, RosterDayCost>();

  for (const shift of shifts) {
    if (shift.staffId == null) continue;
    const staffKey = shift.staffFirestoreId ?? `staff-${shift.staffId}`;
    if (!eligibleKeys.has(staffKey)) continue;
    const staff = staffByKey.get(staffKey);
    const paidHours = paidHoursRounded(shift);
    const cost = paidHours * (staff?.hourlyRate ?? 0);
    const dateKey = dateKeyFromShiftDate(shift.date);
    const existing = totals.get(dateKey) ?? { hours: 0, cost: 0 };
    existing.hours += paidHours;
    existing.cost += cost;
    totals.set(dateKey, existing);
  }

  return totals;
}

/**
 * Same formula as computeRosterCostByDateKey, keyed by shift.day (Mon–Sun).
 * Used by Staff Roster table footer.
 */
export function computeRosterCostByWeekday(
  shifts: readonly Shift[],
  staffMembers: readonly StaffMember[],
  weekDays: readonly Weekday[],
): Record<Weekday, RosterDayCost> {
  const totals = weekDays.reduce(
    (acc, day) => {
      acc[day] = { hours: 0, cost: 0 };
      return acc;
    },
    {} as Record<Weekday, RosterDayCost>,
  );
  const eligible = staffMembers.filter(isRosterEligibleStaff);
  const eligibleKeys = new Set(eligible.map(getStaffRosterKey));
  const staffByKey = new Map(eligible.map((m) => [getStaffRosterKey(m), m]));

  for (const shift of shifts) {
    if (shift.staffId == null) continue;
    const staffKey = shift.staffFirestoreId ?? `staff-${shift.staffId}`;
    if (!eligibleKeys.has(staffKey)) continue;
    const staff = staffByKey.get(staffKey);
    const paidHours = paidHoursRounded(shift);
    const cost = paidHours * (staff?.hourlyRate ?? 0);
    if (!(shift.day in totals)) continue;
    totals[shift.day].hours += paidHours;
    totals[shift.day].cost += cost;
  }

  return totals;
}
